import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading1, Heading2, Quote, Undo2, Redo2, Link as LinkIcon,
  Strikethrough,
} from "lucide-react";
import { useEffect } from "react";

const btnBase =
  "p-1.5 rounded hover:bg-[#0B3676]/10 text-[#374151] transition-colors";
const btnActive = "bg-[#0B3676] text-white hover:bg-[#08285A]";

function ToolbarButton({ onClick, active, disabled, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`${btnBase} ${active ? btnActive : ""} disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

/**
 * RichEditor - TipTap-based word-like editor with bold/italic/underline/lists.
 * Emits HTML string via onChange.
 * @param value  HTML string
 * @param editable  true (write) | false (read-only render)
 */
export default function RichEditor({
  value = "",
  onChange,
  editable = true,
  placeholder = "Yazmaya başlayın...",
  minHeight = 220,
  testid,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editable,
    onUpdate: ({ editor }) => {
      onChange && onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        "data-testid": testid || "rich-editor",
        class:
          "prose prose-sm max-w-none focus:outline-none px-4 py-3 " +
          "prose-headings:text-[#08285A] prose-strong:text-[#111827] " +
          "prose-a:text-[#0B3676] prose-a:font-semibold",
      },
    },
  });

  // Keep editor in sync when value changes externally (e.g., switching viewed report)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== (value || "")) {
      editor.commands.setContent(value || "", false);
    }
    editor.setEditable(editable);
  }, [value, editable, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL:", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div
      className="border border-[#E5E7EB] rounded-lg bg-white overflow-hidden"
      data-testid={`${testid || "rich-editor"}-wrap`}
    >
      {editable && (
        <div className="flex flex-wrap gap-0.5 border-b border-[#E5E7EB] bg-[#F6F8FB] px-2 py-1.5">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            title="Kalın (Ctrl+B)" testid="ed-bold"
          ><Bold size={15} /></ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            title="İtalik (Ctrl+I)" testid="ed-italic"
          ><Italic size={15} /></ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive("underline")}
            title="Altı Çizili (Ctrl+U)" testid="ed-underline"
          ><UnderlineIcon size={15} /></ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive("strike")}
            title="Üstü Çizili"
          ><Strikethrough size={15} /></ToolbarButton>
          <div className="w-px bg-[#E5E7EB] mx-1 my-0.5" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive("heading", { level: 1 })}
            title="Başlık 1"
          ><Heading1 size={15} /></ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive("heading", { level: 2 })}
            title="Başlık 2"
          ><Heading2 size={15} /></ToolbarButton>
          <div className="w-px bg-[#E5E7EB] mx-1 my-0.5" />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            title="Madde İşareti" testid="ed-bullet"
          ><List size={15} /></ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            title="Numaralı Liste" testid="ed-ordered"
          ><ListOrdered size={15} /></ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive("blockquote")}
            title="Alıntı"
          ><Quote size={15} /></ToolbarButton>
          <ToolbarButton
            onClick={setLink}
            active={editor.isActive("link")}
            title="Link Ekle"
          ><LinkIcon size={15} /></ToolbarButton>
          <div className="w-px bg-[#E5E7EB] mx-1 my-0.5" />
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Geri Al (Ctrl+Z)"
          ><Undo2 size={15} /></ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Yinele (Ctrl+Y)"
          ><Redo2 size={15} /></ToolbarButton>
        </div>
      )}
      <div style={{ minHeight }} className="max-h-[60vh] overflow-y-auto azd-scrollbar">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
